import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Offcanvas } from "bootstrap";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import * as caseService from "../Services/caseService";
// import "./Notification.css";

const API_URL = process.env.REACT_APP_URL;


const notificationSocket = io(API_URL, {
  transports: ["polling"],
   autoConnect: false,
  reconnection: false,
  // autoConnect: false,
});

const Notification = forwardRef(({ onCountChange }, ref) => {
  const navigate = useNavigate();

  const offcanvasRef = useRef(null);
  const bsOffcanvas = useRef(null);

  const previousNotificationIds = useRef([]);
  const firstLoad = useRef(true);
  const isNavigatingRef = useRef(false);
  const audioContextRef = useRef(null);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [showCaseOptions, setShowCaseOptions] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);


  useEffect(() => {
    const element = offcanvasRef.current;

    if (!element) {
      return;
    }

    try {
      bsOffcanvas.current = Offcanvas.getOrCreateInstance(element);
    } catch (error) {
      console.error(
        "Notification offcanvas initialization error:",
        error
      );
    }

    return () => {
      try {
        const instance = Offcanvas.getInstance(element);

        if (instance) {
          instance.hide();
        }

        bsOffcanvas.current = null;
      } catch (error) {
        console.error(
          "Notification offcanvas cleanup error:",
          error
        );
      }
    };
  }, []);

  

  const getOffcanvasInstance = () => {
    const element = offcanvasRef.current;

    if (!element) {
      return null;
    }

    try {
      return (
        Offcanvas.getInstance(element) ||
        Offcanvas.getOrCreateInstance(element)
      );
    } catch (error) {
      console.error("Get offcanvas instance error:", error);
      return null;
    }
  };

 
  const hideOffcanvas = () => {
    try {
      const instance = getOffcanvasInstance();

      if (instance) {
        instance.hide();
      }
    } catch (error) {
      console.error(
        "Notification offcanvas hide error:",
        error
      );
    }
  };

 

  const navigateAfterOffcanvasClose = (path, options = {}) => {
    if (!path) {
      return;
    }

    const element = offcanvasRef.current;

    if (!element) {
      navigate(path, options);
      return;
    }

    const instance = getOffcanvasInstance();

    if (!instance) {
      navigate(path, options);
      return;
    }

    if (!element.classList.contains("show")) {
      navigate(path, options);
      return;
    }

    if (isNavigatingRef.current) {
      return;
    }

    isNavigatingRef.current = true;

    const handleHidden = () => {
      try {
        navigate(path, options);
      } finally {
        isNavigatingRef.current = false;
      }
    };

    element.addEventListener(
      "hidden.bs.offcanvas",
      handleHidden,
      {
        once: true,
      }
    );

    instance.hide();
  };

 

  const unlockNotificationAudio = async () => {
    try {
      const AudioContext =
        window.AudioContext ||
        window.webkitAudioContext;

      if (!AudioContext) {
        return;
      }

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }

      if (
        audioContextRef.current.state ===
        "suspended"
      ) {
        await audioContextRef.current.resume();
      }
    } catch (error) {
      console.error(
        "Audio unlock error:",
        error
      );
    }
  };

 

  const playNotificationSound = async () => {
    try {
      const AudioContext =
        window.AudioContext ||
        window.webkitAudioContext;

      if (!AudioContext) {
        console.warn(
          "AudioContext is not supported"
        );
        return;
      }

      if (!audioContextRef.current) {
        audioContextRef.current =
          new AudioContext();
      }

      const audioContext =
        audioContextRef.current;

      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      const playTone = (
        frequency,
        startTime,
        duration,
        volume = 0.6
      ) => {
        const oscillator =
          audioContext.createOscillator();

        const gain =
          audioContext.createGain();

        oscillator.connect(gain);
        gain.connect(
          audioContext.destination
        );

        oscillator.type = "sine";

        oscillator.frequency.setValueAtTime(
          frequency,
          startTime
        );

        gain.gain.setValueAtTime(
          0.001,
          startTime
        );

        gain.gain.exponentialRampToValueAtTime(
          volume,
          startTime + 0.02
        );

        gain.gain.exponentialRampToValueAtTime(
          0.001,
          startTime + duration
        );

        oscillator.start(startTime);

        oscillator.stop(
          startTime + duration + 0.03
        );
      };

      const now =
        audioContext.currentTime;

      playTone(
        850,
        now,
        0.25,
        0.65
      );

      playTone(
        1100,
        now + 0.18,
        0.30,
        0.60
      );
    } catch (error) {
      console.error(
        "Notification sound error:",
        error
      );
    }
  };

 

  const getNotifications = async () => {
    try {
      setLoading(true);

      const response =
        await caseService.getNotifications();

      if (
        response?.data?.status === "success"
      ) {
        const notificationData =
          response?.data?.data || [];

       
        const currentNotificationIds =
          notificationData.map(
            (notification) =>
              notification.id
          );

        
        if (!firstLoad.current) {
          const newNotifications =
            notificationData.filter(
              (notification) =>
                !previousNotificationIds.current.includes(
                  notification.id
                )
            );

          if (
            newNotifications.length > 0
          ) {
            playNotificationSound();
          }
        }

        
        firstLoad.current = false;

        previousNotificationIds.current =
          currentNotificationIds;

        setNotifications(
          notificationData
        );

        const unreadCount =
          notificationData.filter(
            (notification) =>
              !notification.isRead
          ).length;

        onCountChange?.(unreadCount);
      }
    } catch (error) {
      console.error(
        "Notification Error:",
        error
      );
    } finally {
      setLoading(false);
    }
  };





   useEffect(() => {
    let mounted = true;
    
    // first time notification fetch 
    getNotifications();

    const handleNewNotification = () => {
      if (!mounted) return;
      getNotifications();
    };

    const handleConnect = () => {
    //   console.log("Notification socket connected:", notificationSocket.id);
    };

    const handleConnectError = (error) => {
    //   console.error("❌ Notification socket error:", error?.message || error);
    };

    // remove old lister 
    notificationSocket.off("newNotification");
    notificationSocket.off("connect");
    notificationSocket.off("connect_error");

  //  attach new lister 
    notificationSocket.on("newNotification", handleNewNotification);
    notificationSocket.on("connect", handleConnect);
    notificationSocket.on("connect_error", handleConnectError);

    
    if (!notificationSocket.connected) {
      // notificationSocket.connect();
       notificationSocket.connect();
    }

    
    return () => {
      mounted = false;
      notificationSocket.off("newNotification", handleNewNotification);
      notificationSocket.off("connect", handleConnect);
      notificationSocket.off("connect_error", handleConnectError);
    };
  }, []);



  const removeNotification = async (
    notificationId
  ) => {
    if (!notificationId) {
      return;
    }

    try {
      setRemovingId(notificationId);

      const response =
        await caseService.deleteNotification(
          notificationId
        );

      if (
        response?.data?.status ===
        "success"
      ) {
        setNotifications((prev) =>
          prev.filter(
            (notification) =>
              notification.id !==
              notificationId
          )
        );

        previousNotificationIds.current =
          previousNotificationIds.current.filter(
            (id) =>
              id !== notificationId
          );

        /*
         * Update count immediately
         */
        setNotifications((current) => {
          const unreadCount =
            current.filter(
              (notification) =>
                !notification.isRead
            ).length;

          onCountChange?.(
            unreadCount
          );

          return current;
        });
      }
    } catch (error) {
      console.error(
        "Delete notification error:",
        error
      );
    } finally {
      setRemovingId(null);
    }
  };


  const handleNotificationClick = (
    notification
  ) => {
    if (!notification) {
      return;
    }

    if (!notification.caseId) {
      return;
    }

    const accountType =
      String(
        notification.accountType || ""
      )
        .trim()
        .toUpperCase();

    /*
     * B2B
     */
    if (accountType === "B2B") {
      navigate(
        `/b2b/cases/activity/${notification.caseId}`,
        {
          state: {
            caseId:
              notification.caseId,
            fromNotification: true,
          },
        }
      );

      return;
    }

    /*
     * B2C
     */
    if (accountType === "B2C") {
      navigate(
        `/b2c/cases/activity/${notification.caseId}`,
        {
          state: {
            caseId:
              notification.caseId,
            fromNotification: true,
          },
        }
      );

      return;
    }

    /*
     * BOTH
     */
    if (
      accountType === "BOTH" ||
      accountType === "3"
    ) {
      setSelectedNotification(
        notification
      );

      setShowCaseOptions(true);

      return;
    }
  };

  

  const handleBothRedirect = (type) => {
    if (
      !selectedNotification?.caseId
    ) {
      return;
    }

    const caseId =
      selectedNotification.caseId;

    let path = "";

    if (type === "B2B") {
      path = `/b2b/cases/activity/${caseId}`;
    }

    if (type === "B2C") {
      path = `/b2c/cases/activity/${caseId}`;
    }

    if (!path) {
      return;
    }

    setShowCaseOptions(false);

    navigateAfterOffcanvasClose(
      path,
      {
        state: {
          caseId,
          fromNotification: true,
        },
      }
    );

    setSelectedNotification(null);
  };



  useEffect(() => {
    const handleEscape = (event) => {
      if (
        event.key === "Escape" &&
        showCaseOptions
      ) {
        setShowCaseOptions(false);
        setSelectedNotification(null);
      }
    };

    document.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [showCaseOptions]);

  useImperativeHandle(ref, () => ({
    show() {
      try {
        /*
         * Browser audio unlock
         */
        unlockNotificationAudio();

        const instance =
          getOffcanvasInstance();

        if (!instance) {
          return;
        }

        instance.show();
        getNotifications();
      } catch (error) {
        console.error(
          "Notification show error:",
          error
        );
      }
    },

    hide() {
      hideOffcanvas();
    },
  }));


  const formatDate = (date) => {
    if (!date) {
      return "";
    }

    try {
      return new Date(
        date
      ).toLocaleString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      );
    } catch (error) {
      return "";
    }
  };


  const getNotificationIcon = (
    notification
  ) => {
    if (
      notification?.type ===
      "CASE_ASSIGNED"
    ) {
      return "fas fa-briefcase";
    }

    if (
      notification?.type ===
      "CHAT_MESSAGE"
    ) {
      return "fas fa-comment-dots";
    }

    return "fas fa-bell";
  };


  const getIconBackground = (
    notification
  ) => {
    if (
      notification?.type ===
      "CASE_ASSIGNED"
    ) {
      return "#e8f1ff";
    }

    if (
      notification?.type ===
      "CHAT_MESSAGE"
    ) {
      return "#f1eaff";
    }

    return "#fff5db";
  };

  const getIconColor = (
    notification
  ) => {
    if (
      notification?.type ===
      "CASE_ASSIGNED"
    ) {
      return "#2563eb";
    }

    if (
      notification?.type ===
      "CHAT_MESSAGE"
    ) {
      return "#7c3aed";
    }

    return "#d97706";
  };


  const unreadCount =
    notifications.filter(
      (notification) =>
        !notification.isRead
    ).length;


  return (
    <>
    
      <div
        ref={offcanvasRef}
        className="offcanvas offcanvas-end notification-offcanvas"
        tabIndex="-1"
      >
    
        <div className="notification-header">
          <div className="notification-header-inner">
            <div className="notification-header-left">
              <div className="notification-header-icon">
                <i className="fas fa-bell"></i>

                {unreadCount > 0 && (
                  <span className="notification-header-badge">
                    {unreadCount > 99
                      ? "99+"
                      : unreadCount}
                  </span>
                )}
              </div>

              <div className="notification-header-title-wrapper">
                <div className="notification-header-title">
                  Notifications
                </div>

                <div className="notification-header-subtitle">
                  Stay updated with your activity
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={hideOffcanvas}
              aria-label="Close notifications"
              className="notification-close-button"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div className="notification-toolbar">
          <div className="notification-unread-status">
            <span
              className={`notification-status-dot ${
                unreadCount > 0
                  ? "has-unread"
                  : ""
              }`}
            />

            <span className="notification-status-text">
              {unreadCount > 0
                ? `${unreadCount} unread`
                : "You're all caught up"}
            </span>
          </div>

          <button
            type="button"
            onClick={getNotifications}
            disabled={loading}
            className="notification-refresh-button"
          >
            <i
              className={`fas fa-sync-alt me-1 ${
                loading ? "fa-spin" : ""
              }`}
            ></i>

            Refresh
          </button>
        </div>

        <div className="offcanvas-body notification-body">
          {/* =====================================================
              LOADING
          ===================================================== */}

          {loading &&
          notifications.length === 0 ? (
            <div className="notification-loading">
              <div className="spinner-border notification-spinner" />

              <div className="notification-loading-text">
                Loading notifications...
              </div>
            </div>
          ) : notifications.length ===
            0 ? (

            <div className="notification-empty">
              <div className="notification-empty-icon-wrapper">
                <i className="fas fa-bell-slash notification-empty-icon"></i>
              </div>

              <h6 className="notification-empty-title">
                No Notifications
              </h6>

              <p className="notification-empty-text">
                You're all caught up. New
                notifications will appear
                here.
              </p>
            </div>
          ) : (

            <div className="notification-list">
              {notifications.map(
                (notification) => {
                  const isUnread =
                    !notification.isRead;

                  const isRemoving =
                    removingId ===
                    notification.id;

                  return (
                    <div
                      key={
                        notification.id
                      }
                      onClick={() =>
                        handleNotificationClick(
                          notification
                        )
                      }
                      className={`notification-card ${
                        isUnread
                          ? "notification-card-unread"
                          : ""
                      } ${
                        isRemoving
                          ? "notification-card-removing"
                          : ""
                      }`}
                    >
                    
                      <div
                        className="notification-card-icon"
                        style={{
                          background:
                            getIconBackground(
                              notification
                            ),
                        }}
                      >
                        <i
                          className={getNotificationIcon(
                            notification
                          )}
                          style={{
                            color:
                              getIconColor(
                                notification
                              ),
                          }}
                        />
                      </div>

                    
                      <div className="notification-card-content">
                        {/* TITLE */}

                        <div className="notification-card-title-row">
                          <div className="notification-card-title">
                            {
                              notification.title
                            }
                          </div>

                          {isUnread && (
                            <span className="notification-card-unread-dot" />
                          )}
                        </div>

                        {/* MESSAGE */}

                        <div className="notification-card-message">
                          {
                            notification.message
                          }
                        </div>

                        {/* CASE */}

                        {notification.caseId && (
                          <div className="notification-case-badge">
                            <i className="fas fa-hashtag" />

                            {
                              notification.caseId
                            }

                            <i className="fas fa-arrow-right notification-case-arrow" />
                          </div>
                        )}

                        {/* DATE */}

                        <div className="notification-card-date">
                          <i className="far fa-clock" />

                          {formatDate(
                            notification.createdAt
                          )}
                        </div>
                      </div>

                      <button
                        type="button"
                        disabled={
                          isRemoving
                        }
                        title="Remove notification"
                        onClick={(event) => {
                          event.stopPropagation();

                          removeNotification(
                            notification.id
                          );
                        }}
                        className="notification-delete-button"
                      >
                        {isRemoving ? (
                          <span className="spinner-border spinner-border-sm notification-delete-spinner" />
                        ) : (
                          <i className="fas fa-times" />
                        )}
                      </button>


                      {notification.caseId && (
                        <div className="notification-card-arrow">
                          <i className="fas fa-chevron-right" />
                        </div>
                      )}
                    </div>
                  );
                }
              )}
            </div>
          )}
        </div>
      </div>

      {showCaseOptions && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => {
            setShowCaseOptions(false);
            setSelectedNotification(
              null
            );
          }}
          className="notification-modal-overlay"
        >
          <div
            onClick={(event) =>
              event.stopPropagation()
            }
            className="notification-modal"
          >
            <div className="notification-modal-header">
              <div className="notification-modal-header-content">
                <div className="notification-modal-header-icon">
                  <i className="fas fa-folder-open" />
                </div>

                <div>
                  <div className="notification-modal-title">
                    Open Case
                  </div>

                  <div className="notification-modal-subtitle">
                    Case #
                    {
                      selectedNotification?.caseId
                    }
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowCaseOptions(
                    false
                  );
                  setSelectedNotification(
                    null
                  );
                }}
                className="notification-modal-close"
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="notification-modal-body">
              <div className="notification-modal-description">
                This case is available in both
                B2B and B2C. Select where you want
                to open the case.
              </div>

              {/* B2B */}

              <button
                type="button"
                onClick={() =>
                  handleBothRedirect(
                    "B2B"
                  )
                }
                className="notification-account-button notification-account-b2b"
              >
                <div className="notification-account-icon notification-account-icon-b2b">
                  <i className="fas fa-building" />
                </div>

                <div className="notification-account-content">
                  <div className="notification-account-title">
                    Open in B2B
                  </div>

                  <div className="notification-account-description">
                    Open B2B case activity
                  </div>
                </div>

                <i className="fas fa-chevron-right notification-account-arrow" />
              </button>

              {/* B2C */}

              <button
                type="button"
                onClick={() =>
                  handleBothRedirect(
                    "B2C"
                  )
                }
                className="notification-account-button notification-account-b2c"
              >
                <div className="notification-account-icon notification-account-icon-b2c">
                  <i className="fas fa-user" />
                </div>

                <div className="notification-account-content">
                  <div className="notification-account-title">
                    Open in B2C
                  </div>

                  <div className="notification-account-description">
                    Open B2C case activity
                  </div>
                </div>

                <i className="fas fa-chevron-right notification-account-arrow" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default Notification;